import { useMemo } from "react";
import type { EChartsOption } from "echarts";
import { dailyAchievementStats, genderStats } from "../data/analytics";
import { CHART_COLORS } from "../data/constants";
import {
  formatDateTime,
  formatDuration,
  formatNumber,
  formatPercent,
  getBrowserTimeZone,
  median,
} from "../data/formatters";
import type { EventSnapshot, NormalizedRunner } from "../types";
import { Chart, chartGridColor, chartTextStyle } from "../components/Chart";
import { MetricCard } from "../components/MetricCard";
import { Panel } from "../components/Panel";

export function OverviewPage({ snapshot, runners }: { snapshot: EventSnapshot; runners: NormalizedRunner[] }) {
  const daily = useMemo(() => dailyAchievementStats(runners), [runners]);
  const genders = useMemo(() => genderStats(runners), [runners]);
  const timeMedian = median(runners.map((runner) => runner.totalTimeSeconds));
  const activityMedian = median(runners.map((runner) => runner.activityCount));
  const achievementRate = snapshot.participantCount
    ? snapshot.achieverCount / snapshot.participantCount
    : Number.NaN;

  const curveOption = useMemo<EChartsOption>(() => ({
    textStyle: chartTextStyle,
    tooltip: { trigger: "axis", valueFormatter: (value) => `${Number(value).toLocaleString()} 人` },
    legend: { top: 0, textStyle: chartTextStyle },
    grid: { left: 54, right: 22, top: 48, bottom: 58 },
    xAxis: {
      type: "category",
      data: daily.map((row) => row.date.slice(5)),
      axisLabel: { ...chartTextStyle, rotate: 35 },
      axisLine: { lineStyle: { color: chartGridColor } },
    },
    yAxis: [
      { type: "value", name: "每日", axisLabel: chartTextStyle, splitLine: { lineStyle: { color: chartGridColor } } },
      { type: "value", name: "累積", axisLabel: chartTextStyle, splitLine: { show: false } },
    ],
    series: [
      { name: "每日新增", type: "bar", data: daily.map((row) => row.count), itemStyle: { color: CHART_COLORS[0] } },
      { name: "累積達標", type: "line", yAxisIndex: 1, smooth: false, symbol: "rect", symbolSize: 7, data: daily.map((row) => row.cumulative), lineStyle: { color: CHART_COLORS[1], width: 3 }, itemStyle: { color: CHART_COLORS[1] } },
    ],
  }), [daily]);

  const compositionOption = useMemo<EChartsOption>(() => ({
    textStyle: chartTextStyle,
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    grid: { left: 20, right: 20, top: 18, bottom: 32, containLabel: true },
    xAxis: { type: "value", max: Math.max(1, runners.length), show: false },
    yAxis: { type: "category", data: ["篩選後達標者"], axisLabel: chartTextStyle, axisLine: { show: false }, axisTick: { show: false } },
    series: genders.map((group, index) => ({
      name: group.label,
      type: "bar",
      stack: "total",
      data: [group.value],
      label: { show: group.value / Math.max(runners.length, 1) > 0.08, formatter: group.label, color: "#111827", fontSize: 14 },
      itemStyle: { color: CHART_COLORS[index] },
    })),
  }), [genders, runners.length]);

  return (
    <div className="page-stack">
      <div className="metric-grid">
        <MetricCard label="活動參加人數" value={formatNumber(snapshot.participantCount)} note="官方 metadata 母數" tone="blue" />
        <MetricCard label="整體已達標" value={formatNumber(snapshot.achieverCount)} note={`${formatPercent(achievementRate)} 整體達標比例`} tone="mint" />
        <MetricCard label="篩選後樣本" value={formatNumber(runners.length)} note="所有本頁圖表共用此母數" tone="yellow" />
        <MetricCard label="累積活動時間中位數" value={timeMedian == null ? "—" : formatDuration(timeMedian)} note="這不是半馬完賽時間" tone="pink" />
        <MetricCard label="跑步次數中位數" value={activityMedian == null ? "—" : `${activityMedian.toLocaleString()} 次`} note="篩選後達標者" tone="blue" />
        <MetricCard label="目標距離" value={snapshot.targetDistance == null ? "—" : `${snapshot.targetDistance} km`} note={`${snapshot.rankingType}`} tone="mint" />
      </div>

      {snapshot.participantCount != null && (
        <Panel title="參加者與達標者比較" eyebrow="PARTICIPANT PROGRESS" aside={<span className="sample">整體達標比例 {formatPercent(achievementRate)}</span>}>
          <div className="comparison">
            <div className="comparison__labels">
              <span><i className="comparison__achieved" />已達標 <strong>{formatNumber(snapshot.achieverCount)}</strong></span>
              <span><i className="comparison__remaining" />其餘參加者 <strong>{formatNumber(Math.max(0, snapshot.participantCount - snapshot.achieverCount))}</strong></span>
            </div>
            <div className="comparison__track" role="img" aria-label={`活動參加者 ${snapshot.participantCount} 人，其中 ${snapshot.achieverCount} 人已達標`}>
              <span style={{ width: `${Math.min(100, Math.max(0, achievementRate * 100))}%` }} />
            </div>
            <p>只使用活動參加總人數作為分母；性別、年齡與地點只呈現達標者組成。</p>
          </div>
        </Panel>
      )}

      <div className="content-grid content-grid--wide-left">
        <Panel title="達標時間曲線" eyebrow="ACHIEVEMENT TIMELINE" aside={<span className="sample">N = {formatNumber(runners.length)}</span>}>
          {daily.length ? <Chart option={curveOption} label="每日新增與累積達標人數圖" /> : <EmptyState />}
        </Panel>
        <Panel title="達標者性別組成" eyebrow="COMPOSITION" aside={<span className="sample">非達標率</span>}>
          {runners.length ? <Chart option={compositionOption} height={230} label="達標者性別組成堆疊圖" /> : <EmptyState />}
          <ul className="legend-list">
            {genders.map((group, index) => (
              <li key={group.label}><i style={{ background: CHART_COLORS[index] }} /> <span>{group.label}</span><strong>{group.value.toLocaleString()}</strong></li>
            ))}
          </ul>
        </Panel>
      </div>

      <Panel title="資料時間與口徑" eyebrow="SOURCE CLOCK">
        <div className="info-grid">
          <div><span>官方資料彙整</span><strong>{formatDateTime(snapshot.aggregateAt)}</strong></div>
          <div><span>本次系統擷取</span><strong>{formatDateTime(snapshot.fetchedAt)}</strong></div>
          <div><span>顯示時區</span><strong>{getBrowserTimeZone()}</strong></div>
          <div><span>賽事期間</span><strong>{formatDateTime(snapshot.eventStartAt)} → {formatDateTime(snapshot.eventEndAt)}</strong></div>
        </div>
      </Panel>
    </div>
  );
}

function EmptyState() {
  return <div className="empty-state"><span>×</span><p>目前篩選條件沒有資料。</p></div>;
}
